import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { HealthBadge } from '../components/HealthBadge';
import { warmupDisplayDay } from '../live/warmupDay';
import type { MailboxRow } from '../outreachTypes';
import { homeMessages as messages } from './homeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Home.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The mailboxes of the Pitch home (design outreach-v2 i1): the address, the warm-up day and the cap
 * of today, or "Ready · 50 a day", and the health. "Settings" opens Settings → Outreach.
 */
const HomeMailboxesComponent: React.FC<{ mailboxes: MailboxRow[] }> = ({ mailboxes }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openSettings = () => router.push(SMLRoutes.settings, { tab: 'outreach' });
  const rows = useMemo(
    () =>
      mailboxes.map((mailbox) => (
        <div key={mailbox.mailboxUuid} className={styles.mailboxRow} data-testid="outreach-home-mailbox">
          <span className={shared.grow}>
            <b className="d-block text-truncate">{mailbox.address}</b>
            <small className={shared.small}>
              {mailbox.warmup.warming
                ? intl.formatMessage(messages.mailboxWarming, {
                    day: warmupDisplayDay(mailbox.warmup),
                    total: mailbox.warmup.totalDays,
                    cap: mailbox.warmup.todayCap,
                  })
                : intl.formatMessage(messages.mailboxReady, { cap: mailbox.capToday })}
            </small>
          </span>
          <HealthBadge health={mailbox.health} />
        </div>
      )),
    [intl, mailboxes]
  );

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.mailboxes)}
        right={
          <SMLButton size="sm" color="link" omitCap onClick={openSettings}>
            {intl.formatMessage(messages.mailboxSettings)}
          </SMLButton>
        }
      />
      <PanelBody>
        <div className={shared.stack}>
          {mailboxes.length === 0 ? (
            <>
              <p className={shared.small}>{intl.formatMessage(messages.mailboxesNone)}</p>
              <div>
                <SMLButton size="sm" color="primary" omitCap leftIcon={faPlus} onClick={openSettings}>
                  {intl.formatMessage(messages.connectMailbox)}
                </SMLButton>
              </div>
            </>
          ) : (
            rows
          )}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const HomeMailboxes = TypedMemo(HomeMailboxesComponent);
